import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {
    applyToPaperRowDetail,
    computeToPaperPerClient,
    filterGainLossExchangeRowSelectionRows,
    filterTaxWpRowSelectionRows,
    isEmptyRecordSelectionRow,
    isGainLossExchangeCode,
    isPlAeRenCode,
    isPlAeRenSubmenuFetchCode,
    isPpeCategoryFetchCode,
    isPpeOrDepToPaperCode,
    isTaxWpFsCode,
    resolveToPaperWpFetchErrorResult,
    resolveToPaperWpFetchResult,
    resolveVerifyToPaperDetail,
    shouldOfferEmptyRecordRowSelection,
    withEmptyRecordOption,
    type ToPaperWpFetchResult,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/initial-data-factories';
import {
    isNoWpDetailCode,
    resolveToPaperFieldsFromPairing,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {
    INV_ADJUSTMENT_CODE,
    resolveInventoriesToPaperCode,
} from '@/pages/components/audit/adjustment/adjust/utils/companion-codes';

const PPE_WP_FETCH_CODE = 'BS-NCA-PPE';

/** Inventories toPaper：若仍收到 PL-COT-OI/CI，组装时改拉 BS-CA-INV（主路径已在 Drawer/store 归一） */
function resolveInventoriesToPaperFetchCode(
    code: string,
    wpFetchCode?: string,
): string | undefined {
    if (wpFetchCode) return undefined;
    const resolved = resolveInventoriesToPaperCode(code);
    return resolved === INV_ADJUSTMENT_CODE && resolved !== String(code || '').trim().toUpperCase()
        ? INV_ADJUSTMENT_CODE
        : undefined;
}

export type ToPaperAssemblyStatus = 'idle' | 'loading' | 'ready' | 'error' | 'pending_user';

/** UI 弹窗数据源（与 ToPaperWpFetchResult 选择类 outcome 对齐） */
export type ToPaperPendingUserAction =
    | { kind: 'commonInvestmentSelection'; code: string; adjustmentAmount: number; perClient: number }
    | {
        kind: 'arraySelection';
        code: string;
        codeName: string;
        parDetail: any;
        adjustmentAmount: number;
        perClient: number;
        options: Array<{ key: string; label: string }>;
    }
    | {
        kind: 'rowSelection';
        code: string;
        parDetail: any;
        adjustmentAmount: number;
        rows: Array<{
            arrayName: string;
            rowIndex: number;
            rowKey: string;
            perClient: number;
            row: any;
            subField?: string;
        }>;
    }
    | { kind: 'noDirectorConfirm'; code: string }
    | {
        kind: 'mpfSourceEmptyConfirm';
        code: string;
        message: string;
        redirectCode: string;
    };

export type ToPaperAssemblyOutcomeApplyResult =
    | { type: 'ready'; code: string; detail?: any; extraRowKeys?: string[]; flatEquityAdjustment?: number }
    | { type: 'pending'; action: ToPaperPendingUserAction }
    | { type: 'removed'; code: string }
    | { type: 'none' };

export function computeToPaperFieldsForAssembly(
    fromPaperCode: string | undefined,
    fromAdjustment: number,
    _fromEntry: string | null | undefined,
    toCode: string,
    _toEntry: string | null | undefined,
    fsOriginalToEntry?: string | null,
    specialDtdPathCodes?: ReadonlySet<string>,
): { entry: 'Dr' | 'Cr'; adjustment: number } {
    if (fromPaperCode && toCode) {
        return resolveToPaperFieldsFromPairing(
            fromPaperCode,
            fromAdjustment,
            toCode,
            {
                ...(fsOriginalToEntry ? {toOriginalEntry: fsOriginalToEntry} : {}),
                specialDtdPathCodes,
            },
        );
    }
    return {entry: 'Cr', adjustment: 0};
}

export function applyToPaperAssemblyOutcome(
    outcome: ToPaperWpFetchResult,
): ToPaperAssemblyOutcomeApplyResult {
    switch (outcome.kind) {
        case 'none':
            return {type: 'none'};
        case 'commonInvestmentSelection':
            return {
                type: 'pending',
                action: {
                    kind: 'commonInvestmentSelection',
                    code: outcome.code,
                    adjustmentAmount: outcome.adjustmentAmount,
                    perClient: outcome.perClient,
                },
            };
        case 'arraySelection':
            return {
                type: 'pending',
                action: {
                    kind: 'arraySelection',
                    code: outcome.code,
                    codeName: outcome.codeName,
                    parDetail: outcome.parDetail,
                    adjustmentAmount: outcome.adjustmentAmount,
                    perClient: outcome.perClient,
                    options: outcome.options,
                },
            };
        case 'setDetail':
            return {
                type: 'ready',
                code: outcome.code,
                detail: outcome.detail,
                extraRowKeys: outcome.extraRowKeys,
            };
        case 'setFlatEquityDetail':
            return {
                type: 'ready',
                code: outcome.code,
                detail: outcome.detail,
                extraRowKeys: outcome.extraRowKeys,
                flatEquityAdjustment: outcome.adjustment,
            };
        case 'rowSelection': {
            let rows = outcome.rows;
            if (isGainLossExchangeCode(outcome.code)) {
                rows = filterGainLossExchangeRowSelectionRows(outcome.rows, outcome.code);
                if (rows.length === 0) {
                    rows = outcome.rows;
                }
            } else if (isTaxWpFsCode(outcome.code)) {
                // TAXP/TAXR：收窄为 b/f + paid + refunded（通常 3 行 → 弹出 RowSelector）
                // PL-TAX：收窄为 expenses（1 行 → 下方自动绑定）
                rows = filterTaxWpRowSelectionRows(outcome.rows, outcome.code);
                if (rows.length === 0) {
                    rows = outcome.rows;
                }
            }
            // Non-legacy: never auto-bind a single real row — force Empty record + pending.
            if (
                rows.length === 1
                && !isPlAeRenCode(outcome.code)
                && shouldOfferEmptyRecordRowSelection(outcome.code)
                && !isEmptyRecordSelectionRow(rows[0])
            ) {
                return {
                    type: 'pending',
                    action: {
                        kind: 'rowSelection',
                        code: outcome.code,
                        parDetail: outcome.parDetail,
                        adjustmentAmount: outcome.adjustmentAmount,
                        rows: withEmptyRecordOption(outcome.code, rows),
                    },
                };
            }
            if (rows.length === 1 && !isPlAeRenCode(outcome.code)) {
                const singleRow = rows[0];
                const detail = applyToPaperRowDetail(
                    outcome.parDetail,
                    outcome.code,
                    singleRow.arrayName,
                    singleRow.rowIndex,
                    outcome.adjustmentAmount,
                );
                // 仅 PL-AE-MPF：extraRowKeys 带 :mpf（人员单行自动绑定时）
                const extraKey = String(outcome.code || '').trim().toUpperCase() === 'PL-AE-MPF'
                    ? `${outcome.code}:${singleRow.arrayName}:${singleRow.rowIndex}:mpf`
                    : `${outcome.code}:${singleRow.arrayName}:${singleRow.rowIndex}`;
                return {
                    type: 'ready',
                    code: outcome.code,
                    detail,
                    extraRowKeys: [extraKey],
                };
            }
            return {
                type: 'pending',
                action: {
                    kind: 'rowSelection',
                    code: outcome.code,
                    parDetail: outcome.parDetail,
                    adjustmentAmount: outcome.adjustmentAmount,
                    rows: withEmptyRecordOption(outcome.code, rows),
                },
            };
        }
        case 'noDirectorConfirm':
            return {type: 'removed', code: outcome.code};
        case 'mpfSourceEmptyConfirm':
            return {
                type: 'pending',
                action: {
                    kind: 'mpfSourceEmptyConfirm',
                    code: outcome.code,
                    message: outcome.message,
                    redirectCode: outcome.redirectCode,
                },
            };
        default:
            return {type: 'none'};
    }
}

export async function fetchToPaperWpOutcome(
    companyId: number,
    annualId: number,
    code: string,
    calcAdj: number,
    options?: {wpFetchCode?: string; yearOfAssessment?: string | null},
): Promise<ToPaperWpFetchResult> {
    const fsAdjustState = useFsAdjustStore.getState();
    const toLine = fsAdjustState.getLineByCode(code);
    const wpFetchCode = String(options?.wpFetchCode || '').trim() || undefined;
    const assembleYoa = String(options?.yearOfAssessment || '').trim() || null;
    const isRenSubmenu = isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(wpFetchCode);
    const isPpeCategory = isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(wpFetchCode);
    const invToPaperFetch = resolveInventoriesToPaperFetchCode(code, wpFetchCode);
    // PL-AE-REN 二级菜单 discriminator（rental|other）不是真实 WP code，仍拉 PL-AE-REN
    // PPE / DEP 分类 discriminator（xxxRow）不是真实 WP code，统一拉 BS-NCA-PPE
    // Inventories：仅 FsSelectionDrawer 选 PL-COT-OI/CI 为 toPaper 时改拉 BS-CA-INV
    const fetchCode = isRenSubmenu
        ? String(code).trim().toUpperCase()
        : isPpeCategory
            ? PPE_WP_FETCH_CODE
            : invToPaperFetch
                ? invToPaperFetch
                : (String(options?.wpFetchCode || code || '').trim() || code);
    // 仅 REN submenu 需要年度期间展开月份；其它 toPaper 保持原路径（不额外 load annual）
    let periodFrom: string | null = null;
    let periodTo: string | null = null;
    if (isRenSubmenu) {
        const periodInfo = await useCompanyAnnualStore.getState().getPeriodInfo(annualId);
        periodFrom = periodInfo?.periodFrom ?? null;
        periodTo = periodInfo?.periodTo ?? null;
    }
    try {
        const wpData = await fsAdjustState.getWpDetail(
            companyId,
            annualId,
            fetchCode,
            assembleYoa ? {yearOfAssessment: assembleYoa} : undefined,
        );
        const perData = fsAdjustState.getPerData(code, assembleYoa);
        const auditedValue = perData?.auditedValue ?? 0;
        const perClient = computeToPaperPerClient(auditedValue, toLine?.openingAmount);
        return resolveToPaperWpFetchResult({
            code,
            wpData,
            calcAdj,
            toLine,
            perClient,
            wpFetchCode,
            ...(isRenSubmenu ? {periodFrom, periodTo} : {}),
        });
    } catch {
        const perData = fsAdjustState.getPerData(code, assembleYoa);
        const auditedValue = perData?.auditedValue ?? 0;
        const perClient = computeToPaperPerClient(auditedValue, toLine?.openingAmount);
        return resolveToPaperWpFetchErrorResult({
            code,
            wpData: null,
            calcAdj,
            toLine,
            perClient,
            wpFetchCode,
            ...(isRenSubmenu ? {periodFrom, periodTo} : {}),
        });
    }
}

export async function verifyToPaperWpDetail(
    companyId: number,
    annualId: number,
    code: string,
    existingDetail: any,
    options?: {wpFetchCode?: string; yearOfAssessment?: string | null},
): Promise<any | null> {
    const wpFetchCode = String(options?.wpFetchCode || '').trim() || undefined;
    const assembleYoa = String(options?.yearOfAssessment || '').trim() || null;
    const isRenSubmenu = isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(wpFetchCode);
    const isPpeCategory = isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(wpFetchCode);
    const invToPaperFetch = resolveInventoriesToPaperFetchCode(code, wpFetchCode);
    // 默认 NO_WP_DETAIL 的 PL-AE-REN / BS-NCA-PPE：仅分类/二级菜单路径需要 verify
    if (isNoWpDetailCode(code) && !isRenSubmenu && !isPpeCategory) {
        return null;
    }
    const fsAdjustState = useFsAdjustStore.getState();
    const fetchCode = isRenSubmenu
        ? String(code).trim().toUpperCase()
        : isPpeCategory
            ? PPE_WP_FETCH_CODE
            : invToPaperFetch
                ? invToPaperFetch
                : (String(options?.wpFetchCode || code || '').trim() || code);
    try {
        const wpData = await fsAdjustState.getWpDetail(
            companyId,
            annualId,
            fetchCode,
            assembleYoa ? {yearOfAssessment: assembleYoa} : undefined,
        );
        return resolveVerifyToPaperDetail(existingDetail, wpData);
    } catch {
        return resolveVerifyToPaperDetail(existingDetail, null);
    }
}

export function isToPaperAssemblyReadOnlyMode(adjustmentMode: number | null | undefined): boolean {
    return adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;
}
